import {
  DefaultSaleskoLocale,
  type AgentChatInput,
  type AgentChatMention,
  type AgentChatMessage,
  type AgentChatResponse,
  type GraphVisualizationFrame
} from "@salesko/contracts";
import type { AgentChatResult } from "../types";

export type AgentChatHistoryMessage = Pick<AgentChatMessage, "content" | "id" | "role">;

export type NoteIntakeExtractionFailureCopy = {
  base: string;
  reason: (reasons: string) => string;
};

type CreateAgentChatInputOptions = {
  activeNodeId?: string | null;
  /** The displayed frame; only used to resolve @ mentions (may be the demo preview). */
  frame: GraphVisualizationFrame;
  /**
   * The conversation's real generated/committed frame, if any. Only this frame is sent
   * as `currentFrame` for server-side accumulation — sending the demo preview frame
   * here would merge demo entities into the user's first real graph.
   */
  persistedFrame: GraphVisualizationFrame | null;
  history: AgentChatHistoryMessage[];
  message: string;
  /** Defaults to "graph-copilot" so existing writer-mode callers are unaffected. */
  mode?: AgentChatInput["mode"];
  threadId: string;
  userMessageId: string;
};

export function createAgentChatInput({
  activeNodeId,
  frame,
  persistedFrame,
  history,
  message,
  mode = "graph-copilot",
  threadId,
  userMessageId
}: CreateAgentChatInputOptions): AgentChatInput {
  const mentions = mentionsFromCurrentFrame(message, frame);
  const messages: AgentChatInput["messages"] = [
    ...history
      .filter((entry) => entry.content.trim().length > 0)
      .map((entry) => ({
        id: entry.id,
        role: entry.role,
        content: entry.content
      })),
    {
      id: userMessageId,
      role: "user",
      content: message,
      mentions
    }
  ];

  return {
    threadId,
    messages,
    locale: DefaultSaleskoLocale,
    mode,
    ...(persistedFrame ? { currentFrameId: persistedFrame.frameId, currentFrame: persistedFrame } : {}),
    ...(activeNodeId ? { activeNodeId } : {}),
    mentions
  };
}

export function mentionsFromCurrentFrame(message: string, frame: GraphVisualizationFrame): AgentChatMention[] {
  const mentions: AgentChatMention[] = [];
  const seen = new Set<string>();

  for (const node of frame.graph.nodes) {
    const token = `@${node.label}`;
    const start = message.indexOf(token);
    if (start < 0) {
      continue;
    }

    const key = `${node.id}:${start}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);

    mentions.push({
      nodeId: node.id,
      label: node.label,
      entityType: node.type,
      sourceFrameId: frame.frameId,
      range: {
        start,
        end: start + token.length
      }
    });
  }

  return mentions;
}

/**
 * Builds the user-visible error when the agent stream ends without a usable
 * LLM-extracted frame. Fail-closed: this path never persists the note. Any
 * provider-auth / stream failures collected during the stream (`chat.error`
 * events, non-2xx HTTP responses) are surfaced here instead of being discarded,
 * so an authentication failure is not masked as a generic "no frame" outcome.
 */
export function noteIntakeExtractionFailureMessage(
  warnings: readonly string[],
  copy: NoteIntakeExtractionFailureCopy
): string {
  const reasons = warnings.map((entry) => entry.trim()).filter((entry) => entry.length > 0);
  if (reasons.length === 0) {
    return copy.base;
  }

  return `${copy.base} ${copy.reason(reasons.join("; "))}`;
}

export type ResolveSeekerTurnResultInput = {
  doneResponse: AgentChatResponse | undefined;
  assistantContent: string;
  warnings: string[];
  streamFailedMessage: string;
};

/**
 * Fail-closed seeker-turn settlement, split out as a pure function so the failure
 * matrix is unit-testable without standing up the full mutation/stream plumbing.
 *
 * The backend only ever sends chat.done (success) or chat.error (failure) for a seeker
 * turn, never both — apps/agent/src/chat.ts's seeker branch returns immediately after
 * chat.done, and any throw before that point (or a fetch-level failure) is caught by
 * the outer catch, which sends chat.error instead. So "doneResponse is defined and no
 * warnings were captured" is the only success shape. Partial chat.text_delta text that
 * streamed in before a later chat.error/disconnect must not be mistaken for a completed
 * discussion turn just because assistantContent happens to be non-empty.
 */
export function resolveSeekerTurnResult({
  assistantContent,
  doneResponse,
  streamFailedMessage,
  warnings
}: ResolveSeekerTurnResultInput): Extract<AgentChatResult, { mode: "seeker" }> {
  if (!doneResponse || warnings.length > 0) {
    throw new Error(warnings.length > 0 ? warnings.join("; ") : streamFailedMessage);
  }

  const seekerResult = {
    assistantContent,
    mode: "seeker" as const,
    warnings: doneResponse.warnings ?? []
  };
  return doneResponse.source ? { ...seekerResult, source: doneResponse.source } : seekerResult;
}
